import {
    isLightreach,
    lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/productsFinanceHubHelpers';
import {
    computeSystemDesignMissingItems,
    partnerSyncBlockedReasonFromItems,
    quoteBlockedReasonFromItems,
    type SystemDesignMissingItem,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/systemDesignMissingItems';
import { useOneButtonStore } from '../../store/oneButtonStore';
import { useViewerStore } from '../../store/store';
import type { ProductGenerateStep, ProductGenerateStepId } from './preContractProductGeneratePipeline';
import type { PreContractProduct } from './preContractTypes';

const CHECKLIST_STEPS = new Set<ProductGenerateStepId>(['lead', 'utility', 'leadSystem']);

function opportunityProductRows(details: unknown): Record<string, unknown>[] {
    const root = details as { OpportunityProduct?: unknown; opportunityProduct?: unknown } | null;
    const raw = root?.OpportunityProduct ?? root?.opportunityProduct ?? [];
    return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [];
}

function activeProductPartner(
    details: unknown,
    product: PreContractProduct,
): string {
    const match = opportunityProductRows(details).find((row) => {
        const name = String(row.Product_Type_Name ?? row.product_Type_Name ?? '').trim();
        if (name.toLowerCase() !== product.toLowerCase()) return false;
        return row.ActiveFlag !== false && row.activeFlag !== false;
    });
    return String(
        match?.Finance_Partner_Display_Name ??
            match?.finance_Partner_Display_Name ??
            match?.Finance_Partner ??
            '',
    ).trim();
}

/** Related LightReach lines that Create Lead System can include in one payload. */
function relatedLightreachProducts(product: PreContractProduct): PreContractProduct[] {
    if (product === 'Solar') return ['Battery', 'Roof'];
    if (product === 'Battery') return ['Solar'];
    return [];
}

/**
 * Same Partner Sync design gates as V2, plus LightReach Solar/Battery:
 * lead-system cannot run when another LightReach product has no saved design.
 */
export function pciPartnerChecklistBlockedReason(input: {
    product: PreContractProduct;
    partnerName: string;
    stepId: ProductGenerateStepId;
    missingItems: SystemDesignMissingItem[];
    opportunityDetails: unknown;
}): string | null {
    if (!CHECKLIST_STEPS.has(input.stepId)) {
        if (input.stepId === 'quote') {
            return quoteBlockedReasonFromItems(input.product, input.missingItems);
        }
        return null;
    }

    const own = partnerSyncBlockedReasonFromItems(input.product, input.missingItems);
    if (own) return own;

    if (input.stepId !== 'leadSystem' || !isLightreach(input.partnerName)) return null;

    if (
        input.product === 'Battery' &&
        lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts(
            opportunityProductRows(input.opportunityDetails),
        )
    ) {
        return 'Battery Lead System uses the Solar LightReach lead. Complete Solar required steps first.';
    }

    for (const related of relatedLightreachProducts(input.product)) {
        const relatedPartner = activeProductPartner(input.opportunityDetails, related);
        if (!relatedPartner || !isLightreach(relatedPartner)) continue;
        const relatedReason = partnerSyncBlockedReasonFromItems(related, input.missingItems);
        if (relatedReason) return relatedReason;
    }

    return null;
}

export function resolvePciPartnerChecklistBlockedReason(
    product: PreContractProduct,
    partnerName: string,
    stepId: ProductGenerateStepId,
): string | null {
    const store = useOneButtonStore.getState();
    const items = computeSystemDesignMissingItems({
        opportunityDetails: store.opportunityDetails,
        solarUtilityData: store.solarUtilityData,
        utilityCompanyName: store.utilityCompanyName,
        utilityDetailsDirty: store.utilityDetailsDirty,
        sfAnnualUsage: useViewerStore.getState().SFannualUsage,
        systemDesign: store.systemDesign,
        systemDesignLosses: store.systemDesignLosses,
        priceQuoteOverrides: store.priceQuoteOverrides,
        priceQuoteDetails: store.priceQuoteDetails,
        hoverJobId: store.hoverJobId,
    });
    return pciPartnerChecklistBlockedReason({
        product,
        partnerName,
        stepId,
        missingItems: items,
        opportunityDetails: store.opportunityDetails,
    });
}

export function applyPartnerSyncGatesToGenerateSteps(
    steps: ProductGenerateStep[],
    product: PreContractProduct,
    partnerName: string,
    missingItems: SystemDesignMissingItem[],
    opportunityDetails: unknown,
): ProductGenerateStep[] {
    return steps.map((step) => {
        if (step.status === 'running' || step.status === 'synced' || step.status === 'done') {
            return { ...step, blockedReason: undefined };
        }
        const blockedReason = pciPartnerChecklistBlockedReason({
            product,
            partnerName,
            stepId: step.id,
            missingItems,
            opportunityDetails,
        });
        if (!blockedReason) return { ...step, blockedReason: undefined };
        return {
            ...step,
            status: step.status === 'error' ? 'pending' : step.status,
            detail: blockedReason,
            blockedReason,
        };
    });
}
